import React from 'react';
import { motion, Variants } from 'framer-motion';
import './ActivityFeed.css';
import SkeletonLoader from './SkeletonLoader';

interface Activity {
  id: string;
  type: 'sale' | 'purchase' | 'return' | 'stock';
  description: string;
  amount?: string;
  timestamp: string;
  icon: string;
  color: string;
}

interface ActivityFeedProps {
  activities?: Activity[];
  loading?: boolean;
  maxItems?: number;
}

/* =========================
   ANIMATION VARIANTS
========================= */

const containerVariants: Variants = {
  hidden: { opacity: 0 },

  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.15,
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, x: -20 },

  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.4,
      ease: [0.16, 1, 0.3, 1], // ✅ fixed TS + smoother feel
    },
  },
};

const ActivityFeed: React.FC<ActivityFeedProps> = ({
  activities = [
    {
      id: '1',
      type: 'sale',
      description: 'Sale of Aspirin x10 to Customer #1234',
      amount: '+Rs. 1,200',
      timestamp: '2 minutes ago',
      icon: 'bx-shopping-bag',
      color: '#0284c7',
    },
    {
      id: '2',
      type: 'purchase',
      description: 'Purchase from Supplier A - 50 units',
      amount: '-Rs. 5,000',
      timestamp: '15 minutes ago',
      icon: 'bx-cart',
      color: '#06b6d4',
    },
    {
      id: '3',
      type: 'stock',
      description: 'Stock alert: Paracetamol below shelf level',
      timestamp: '1 hour ago',
      icon: 'bx-alert-triangle',
      color: '#f59e0b',
    },
    {
      id: '4',
      type: 'return',
      description: 'Return of expired Ibuprofen x5',
      amount: '-Rs. 500',
      timestamp: '3 hours ago',
      icon: 'bx-undo',
      color: '#ef4444',
    },
  ],
  loading = false,
  maxItems = 5,
}) => {
  /* =========================
     LOADING STATE
  ========================= */

  if (loading) {
    return (
      <div className="activity-feed activity-feed-loading">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="activity-item">
            <SkeletonLoader type="avatar" />
            <div style={{ flex: 1 }}>
              <SkeletonLoader type="text" width="70%" height="14px" />
              <SkeletonLoader type="text" width="50%" height="12px" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  const displayActivities = activities.slice(0, maxItems);

  /* =========================
     MAIN COMPONENT
  ========================= */

  return (
    <motion.div
      className="activity-feed"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="activity-timeline">
        {displayActivities.map((activity, index) => (
          <motion.div
            key={activity.id}
            className="activity-item"
            variants={itemVariants}
            whileHover={{
              x: 4,
              transition: { duration: 0.2, ease: [0.16, 1, 0.3, 1] },
            }}
          >
            {/* ICON DOT */}
            <div className="activity-timeline-dot" style={{ backgroundColor: activity.color }}>
              <i className={`bx ${activity.icon}`} />
            </div>

            {/* TIMELINE LINE */}
            {index < displayActivities.length - 1 && (
              <div className="activity-timeline-line" style={{ backgroundColor: activity.color }} />
            )}

            {/* CONTENT */}
            <div className="activity-content">
              <div className="activity-header">
                <p className="activity-description">{activity.description}</p>
                <span className="activity-timestamp">{activity.timestamp}</span>
              </div>

              {activity.amount && (
                <p
                  className={`activity-amount ${
                    activity.type === 'sale' ? 'positive' : 'negative'
                  }`}
                >
                  {activity.amount}
                </p>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* FOOTER */}
      {activities.length > maxItems && (
        <div className="activity-footer">
          <button className="btn-view-all">
            View All Activities
            <i className="bx bx-chevron-right" />
          </button>
        </div>
      )}
    </motion.div>
  );
};

export default ActivityFeed;
